(() => {
  const initializedSections = new WeakSet();

  function initializeMembershipGallery(section) {
    if (initializedSections.has(section)) return;

    const dialog = section.querySelector("[data-sotm-membership-lightbox]");
    const triggers = [...section.querySelectorAll("[data-sotm-membership-zoom-open]")];
    const image = dialog?.querySelector("[data-sotm-membership-lightbox-image]");
    const caption = dialog?.querySelector("[data-sotm-membership-lightbox-caption]");
    const count = dialog?.querySelector("[data-sotm-membership-lightbox-count]");
    const stage = dialog?.querySelector("[data-sotm-membership-lightbox-stage]");
    const previousButton = dialog?.querySelector("[data-sotm-membership-lightbox-previous]");
    const nextButton = dialog?.querySelector("[data-sotm-membership-lightbox-next]");

    if (!dialog || !image || triggers.length === 0) return;

    initializedSections.add(section);
    let activeIndex = 0;
    let opener = null;
    let touchStartX = null;
    let touchStartY = null;
    let preloadTimer = 0;

    const normalizeIndex = (index) => ((index % triggers.length) + triggers.length) % triggers.length;

    const preloadNext = () => {
      if (triggers.length < 2 || navigator.connection?.saveData) return;

      const nextSource = triggers[normalizeIndex(activeIndex + 1)]?.dataset.imageSrc;
      if (!nextSource) return;

      const preload = () => {
        const nextImage = new Image();
        nextImage.decoding = "async";
        nextImage.src = nextSource;
      };

      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(preload, { timeout: 1200 });
      } else {
        preloadTimer = window.setTimeout(preload, 350);
      }
    };

    const showImage = (index) => {
      activeIndex = normalizeIndex(index);
      const trigger = triggers[activeIndex];
      const source = trigger.dataset.imageSrc;
      const label = trigger.dataset.imageLabel || `Image ${activeIndex + 1}`;
      const alt = trigger.dataset.imageAlt || label;

      if (!source) return;

      dialog.classList.add("is-loading");
      image.alt = alt;
      image.src = source;
      if (caption) caption.textContent = label;
      if (count) count.textContent = `${activeIndex + 1} / ${triggers.length}`;

      if (image.complete && image.naturalWidth > 0) {
        dialog.classList.remove("is-loading");
      }
    };

    const openDialog = (trigger) => {
      opener = trigger;
      showImage(triggers.indexOf(trigger));

      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    };

    const closeDialog = () => {
      if (typeof dialog.close === "function") {
        if (dialog.open) dialog.close();
      } else {
        dialog.removeAttribute("open");
        opener?.focus();
        opener = null;
      }
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => openDialog(trigger));
    });

    previousButton?.addEventListener("click", () => showImage(activeIndex - 1));
    nextButton?.addEventListener("click", () => showImage(activeIndex + 1));

    image.addEventListener("load", () => {
      dialog.classList.remove("is-loading");
      window.clearTimeout(preloadTimer);
      preloadNext();
    });
    image.addEventListener("error", () => dialog.classList.remove("is-loading"));

    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showImage(activeIndex - 1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        showImage(activeIndex + 1);
      } else if (event.key === "Home") {
        event.preventDefault();
        showImage(0);
      } else if (event.key === "End") {
        event.preventDefault();
        showImage(triggers.length - 1);
      } else if (event.key === "Escape" && typeof dialog.close !== "function") {
        event.preventDefault();
        closeDialog();
      }
    });
    dialog.addEventListener("close", () => {
      window.clearTimeout(preloadTimer);
      opener?.focus();
      opener = null;
    });

    stage?.addEventListener("touchstart", (event) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });
    stage?.addEventListener("touchend", (event) => {
      if (touchStartX === null || touchStartY === null || event.changedTouches.length !== 1) return;

      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      showImage(activeIndex + (deltaX < 0 ? 1 : -1));
    }, { passive: true });
  }

  function initializeAll(root = document) {
    root.querySelectorAll?.("[data-sotm-membership]").forEach(initializeMembershipGallery);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
